import React, { useState, useEffect } from 'react';
import { Users, Calendar, Video, MessageSquare, Heart, UserPlus, Activity } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../components/AdminLayout';
import DashboardCard from '../components/DashboardCard';
import DashboardChart from '../components/DashboardChart';

interface Stats {
  totalUsers: number;
  totalEvents: number;
  totalLiveStreams: number;
  totalMessages: number;
  totalPrayerRequests: number;
  totalSmallGroups: number;
  totalMinistries: number;
  userGrowth: number;
  eventGrowth: number;
  prayerGrowth: number;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    totalEvents: 0,
    totalLiveStreams: 0,
    totalMessages: 0,
    totalPrayerRequests: 0,
    totalSmallGroups: 0,
    totalMinistries: 0,
    userGrowth: 0,
    eventGrowth: 0,
    prayerGrowth: 0
  });
  const [loading, setLoading] = useState(true);
  const [activityData, setActivityData] = useState<any[]>([]);

  useEffect(() => {
    fetchStats();
    fetchActivityData();
  }, []);

  async function fetchStats() {
    try {
      const [
        { count: usersCount },
        { count: eventsCount },
        { count: liveStreamsCount },
        { count: messagesCount },
        { count: prayerRequestsCount },
        { count: smallGroupsCount },
        { count: ministriesCount }
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }),
        supabase.from('events').select('*', { count: 'exact', head: true }),
        supabase.from('live_streams').select('*', { count: 'exact', head: true }),
        supabase.from('messages').select('*', { count: 'exact', head: true }),
        supabase.from('prayer_requests').select('*', { count: 'exact', head: true }),
        supabase.from('small_groups').select('*', { count: 'exact', head: true }),
        supabase.from('ministries').select('*', { count: 'exact', head: true })
      ]);

      // Calcular crescimento (exemplo - você precisará implementar a lógica real)
      const userGrowth = 15; // Crescimento de 15% em relação ao mês anterior
      const eventGrowth = 8;
      const prayerGrowth = 12;

      setStats({
        totalUsers: usersCount || 0,
        totalEvents: eventsCount || 0,
        totalLiveStreams: liveStreamsCount || 0,
        totalMessages: messagesCount || 0,
        totalPrayerRequests: prayerRequestsCount || 0,
        totalSmallGroups: smallGroupsCount || 0,
        totalMinistries: ministriesCount || 0,
        userGrowth,
        eventGrowth,
        prayerGrowth
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchActivityData() {
    // Exemplo de dados de atividade - substitua por dados reais do banco
    const data = [
      { name: 'Jan', users: 400 },
      { name: 'Fev', users: 300 },
      { name: 'Mar', users: 600 },
      { name: 'Abr', users: 800 },
      { name: 'Mai', users: 1000 },
      { name: 'Jun', users: 1200 },
    ];
    setActivityData(data);
  }

  const cards = [
    {
      title: 'Usuários',
      value: stats.totalUsers,
      icon: Users,
      color: 'bg-blue-100 text-blue-600',
      change: { value: stats.userGrowth, type: 'increase' as const }
    },
    {
      title: 'Eventos',
      value: stats.totalEvents,
      icon: Calendar,
      color: 'bg-green-100 text-green-600',
      change: { value: stats.eventGrowth, type: 'increase' as const }
    },
    {
      title: 'Transmissões',
      value: stats.totalLiveStreams,
      icon: Video,
      color: 'bg-red-100 text-red-600'
    },
    {
      title: 'Mensagens',
      value: stats.totalMessages,
      icon: MessageSquare,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Pedidos de Oração',
      value: stats.totalPrayerRequests,
      icon: Heart,
      color: 'bg-pink-100 text-pink-600',
      change: { value: stats.prayerGrowth, type: 'increase' as const }
    },
    {
      title: 'Pequenos Grupos',
      value: stats.totalSmallGroups,
      icon: Users,
      color: 'bg-amber-100 text-amber-600'
    },
    {
      title: 'Ministérios',
      value: stats.totalMinistries,
      icon: UserPlus,
      color: 'bg-teal-100 text-teal-600'
    }
  ];

  return (
    <AdminLayout title="Painel Administrativo">
      {/* Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <DashboardCard
            key={card.title}
            title={card.title}
            value={card.value}
            icon={card.icon}
            color={card.color}
            loading={loading}
            change={card.change}
          />
        ))}
      </div>

      {/* Charts */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <DashboardChart 
          title="Crescimento de Usuários" 
          data={activityData} 
          dataKey="users" 
        />

        <DashboardChart 
          title="Pedidos de Oração" 
          data={activityData} 
          dataKey="users" 
          color="#EC4899"
        />
      </div>

      {/* Recent Activity */}
      <div className="mt-8 bg-white p-6 rounded-xl shadow-elevation-1">
        <h2 className="text-xl font-semibold text-brand-teal mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Atividade Recente
        </h2>
        <div className="space-y-4">
          {/* Exemplo de atividades recentes */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="bg-blue-100 p-2 rounded-lg">
              <UserPlus className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium">Novo usuário registrado</p>
              <p className="text-sm text-gray-600">Há 5 minutos</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="bg-green-100 p-2 rounded-lg">
              <Calendar className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium">Novo evento criado</p>
              <p className="text-sm text-gray-600">Há 1 hora</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="bg-red-100 p-2 rounded-lg">
              <Heart className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="font-medium">Novo pedido de oração</p>
              <p className="text-sm text-gray-600">Há 2 horas</p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}